import { apiPost } from "./api";
import type { PortalConfig } from "./config";
import { money } from "./format";
import { confirmBox } from "./confirm";

/** `cost` is label 2's price alone; the spare return label is pay-on-use and never priced in (owner, 2026-11-04). */
interface Quote { ticketNo: number; method: string; cost: number; outbound?: boolean; spare?: boolean }
interface Problem { ticketNo: number; error: string }
interface PrintResult { url: string | null; labels: number; tickets: number; awaitingPickup: number[]; noShippingLabel: number[]; noReturnLabel: number[]; failed: number[] }

const QUOTE_BATCH = 12;
const BUY_BATCH = 4;
const chunks = <T,>(a: T[], n: number) => Array.from({ length: Math.ceil(a.length / n) }, (_, i) => a.slice(i * n, i * n + n));
const plural = (k: number, word: string) => `${k} ${word}${k === 1 ? "" : "s"}`;
const list = (nos: number[]) => `#${nos.join(", #")}`;

/** What printing did: the notice, and the PDF's address for an inline download link (null: nothing printed). */
export interface PrintOutcome { text: string; pdfUrl: string | null }

const PDF_FETCH_TIMEOUT_MS = 30_000;

/** Writes a short note into the blank tab opened on the click, so it doesn't sit there as an empty "about:blank". */
export function holdTab(tab: Window | null, text = "Preparing shipping labels…"): void {
  try {
    if (!tab || tab.closed) return;
    tab.document.title = "Shipping labels";
    tab.document.body.innerHTML = "";
    const p = tab.document.createElement("p");
    p.style.cssText = "font: 16px system-ui, sans-serif; margin: 2em; color: #444";
    p.textContent = `${text} If the portal asks you to confirm, switch back to it. The labels open here when ready.`;
    tab.document.body.appendChild(p);
  } catch { /* not ours to write to: the download link still works */ }
}

/**
 * Shows the merged labels PDF (owner, 2026-11-04: the tab used to stay "about:blank"). The PDF is fetched here and
 * the tab opened on the click is pointed at a blob: copy of it; if that fetch fails, the tab goes to the PDF's own
 * address. Either way the address comes back for an inline "Download labels (PDF)" link, so the labels can always
 * be reached: a blocked popup, a browser pane that can't show PDFs, or a tab that never loads.
 */
export async function openLabelsPdf(url: string, tab: Window | null): Promise<string> {
  let shown = url;
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(PDF_FETCH_TIMEOUT_MS) });
    if (!res.ok) throw new Error(String(res.status));
    shown = URL.createObjectURL(new Blob([await res.arrayBuffer()], { type: "application/pdf" }));
  } catch { /* e.g. the files bucket doesn't allow this page to read it: open the address itself */ }
  try {
    if (tab && !tab.closed) tab.location.href = shown;
  } catch { /* the tab went away: the link still works */ }
  return shown;
}

/**
 * Print Shipping Labels (ticket list and ticket page, owner 2026-11-03): buys labels 2 and 3 through
 * Shippo for tickets that have none (after a confirmation with the count and label 2's cost), then prints labels
 * 2 and 3 for every ticket; unshipped tickets move to Awaiting Pickup. `tab` is opened by the caller
 * at click time, so the browser does not block it; it is closed again if nothing is printed or something fails.
 */
export async function printShippingLabels(config: PortalConfig, token: string, ticketNos: number[], tab: Window | null,
  progress: (text: string) => void = () => undefined): Promise<PrintOutcome> {
  holdTab(tab);
  try {
    progress("Checking labels…");
    const toBuy: Quote[] = [];
    const problems: Problem[] = [];
    let testMode = true;
    for (const batch of chunks(ticketNos, QUOTE_BATCH)) {
      const q = await apiPost<{ toBuy: Quote[]; problems: Problem[]; testMode: boolean }>(config, token, "/tickets/actions/label-quote", { ticketNos: batch });
      toBuy.push(...q.toBuy);
      problems.push(...q.problems);
      testMode = q.testMode;
    }
    let bought = 0;
    if (toBuy.length) {
      const ok = await confirmBox(buyMessage(toBuy, testMode), { title: "Buy labels", ok: "Buy labels" });
      if (!ok) { tab?.close(); return { text: "Nothing printed: no labels were bought.", pdfUrl: null }; }
      let done = 0;
      for (const batch of chunks(toBuy.map((q) => q.ticketNo), BUY_BATCH)) {
        progress(`Buying labels… ${done}/${toBuy.length}`);
        const r = await apiPost<{ bought: number[]; problems: Problem[] }>(config, token, "/tickets/actions/buy-labels", { ticketNos: batch });
        bought += r.bought.length;
        problems.push(...r.problems);
        done += batch.length;
      }
    }
    progress("Preparing the PDF…");
    const p = await apiPost<PrintResult>(config, token, "/tickets/actions/labels", { ticketNos });
    let pdfUrl: string | null = null;
    if (p.url) {
      progress("Opening the PDF…");
      pdfUrl = await openLabelsPdf(p.url, tab);
    } else tab?.close();
    const parts = [
      bought ? `Bought labels for ${plural(bought, "ticket")}.` : "",
      p.labels ? `Printed ${plural(p.labels, "label")} for ${plural(p.tickets, "ticket")}.` : "Nothing to print.",
      p.awaitingPickup.length ? `${plural(p.awaitingPickup.length, "ticket")} moved to Awaiting Pickup.` : "",
      problems.length ? `Problems: ${problems.map((x) => `#${x.ticketNo} ${x.error}`).join("; ")}` : "",
      p.failed.length ? `Could not download: ${list(p.failed)}.` : "",
    ];
    return { text: parts.filter(Boolean).join(" "), pdfUrl };
  } catch (e) {
    tab?.close();
    throw e;
  }
}

/**
 * The "Buy labels" question (owner, 2026-11-04): each ticket with label 2's price only, the total of those, and the
 * spare return label mentioned on its own, since it is charged only if the customer uses it.
 */
export function buyMessage(toBuy: Quote[], testMode: boolean): string {
  const line = (q: Quote) => q.outbound === false ? `#${q.ticketNo}: spare return label only` : `#${q.ticketNo}: ${q.method}, ${money(q.cost)}`;
  const lines = toBuy.slice(0, 12).map(line).join("\n");
  const priced = toBuy.filter((q) => q.outbound !== false);
  const total = priced.reduce((s, q) => s + q.cost, 0);
  const spares = toBuy.filter((q) => q.spare !== false).length;
  const sum = priced.length ? `Total about ${money(total)} for ${priced.length === 1 ? "label 2 to the customer" : `${priced.length} labels to the customers`}.` : "";
  const spare = spares ? `Plus ${spares === 1 ? "a spare return label" : `${spares} spare return labels`}, ${spares === 1 ? "" : "each "}charged only if the customer uses it.` : "";
  return `Buy shipping labels for ${plural(toBuy.length, "ticket")} through Shippo?\n\n${lines}${toBuy.length > 12 ? "\n…" : ""}\n\n` +
    [sum, spare].filter(Boolean).join(" ") +
    (testMode ? "\n\nTest mode: these are Shippo test labels, nothing is charged and they cannot be used." : "");
}
